import React, { useState, useEffect } from 'react';
import { FaSearch, FaVolumeUp, FaCopy, FaExternalLinkAlt } from 'react-icons/fa';

export default function Dictionary() {
  const [word, setWord] = useState('');
  const [translation, setTranslation] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);

  // Carregar histórico do localStorage ao iniciar
  useEffect(() => {
    const savedHistory = localStorage.getItem('dictionaryHistory');
    if (savedHistory) {
      setHistory(JSON.parse(savedHistory));
    }
  }, []);

  // Salvar histórico no localStorage quando ele for atualizado
  useEffect(() => {
    if (history.length > 0) {
      localStorage.setItem('dictionaryHistory', JSON.stringify(history));
    }
  }, [history]);

  const searchReverso = async (e) => {
    e.preventDefault();
    if (!word.trim()) return;
    
    const searchTerm = word.trim().toLowerCase();
    
    // Add to history with a temporary translation
    const tempId = Date.now();
    setHistory(prev => [
      { 
        id: tempId,
        word: searchTerm, 
        translation: 'Traduzindo...',
        englishWord: ''
      },
      ...prev.filter(item => item.word.toLowerCase() !== searchTerm)
    ].slice(0, 5)); // Limita a 5 itens no histórico
    
    // Tentar obter tradução da API do Google Tradutor
    try {
      const response = await fetch(`https://translate.googleapis.com/translate_a/single?client=gtx&sl=pt&tl=en&dt=t&q=${encodeURIComponent(searchTerm)}`);
      const data = await response.json();
      const englishWord = data[0]?.[0]?.[0] || searchTerm;
      
      // Atualiza o histórico com a tradução
      setHistory(prev => prev.map(item => 
        item.id === tempId 
          ? { ...item, translation: englishWord, englishWord }
          : item
      ));
    } catch (error) {
      console.error('Erro na tradução:', error);
      // Se a tradução falhar, mostra apenas a palavra em português
      setHistory(prev => prev.map(item => 
        item.id === tempId 
          ? { ...item, translation: searchTerm, englishWord: searchTerm }
          : item
      ));
    }
    
    // Clear the input
    setWord('');
  };

  const speakWord = (text, lang = 'en-US') => {
    if ('speechSynthesis' in window) {
      // Cancel any ongoing speech
      window.speechSynthesis.cancel();
      
      // Get the English word if available, otherwise use the provided text
      const wordToSpeak = text.englishWord || text;
      const utterance = new SpeechSynthesisUtterance(wordToSpeak);
      utterance.lang = 'en-US';
      
      // Try to find a good English voice
      const voices = window.speechSynthesis.getVoices();
      const englishVoice = voices.find(voice => 
        voice.lang.startsWith('en-') && voice.name.includes('English')
      );
      
      if (englishVoice) {
        utterance.voice = englishVoice;
      }
      
      // Set a natural speaking rate
      utterance.rate = 0.9;
      
      window.speechSynthesis.speak(utterance);
    } else {
      alert('Your browser does not support speech synthesis.');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        // Feedback visual opcional
        const buttons = document.querySelectorAll('.copy-btn');
        buttons.forEach(btn => {
          if (btn.textContent.includes(text)) {
            const originalText = btn.innerHTML;
            btn.innerHTML = '<FaCopy /> Copied!';
            setTimeout(() => {
              btn.innerHTML = originalText;
            }, 2000);
          }
        });
      })
      .catch(err => console.error('Erro ao copiar:', err));
  };

  return (
    <div className="dictionary">
      <div className="dictionary-header">
        <h3>Dictionary PT → EN</h3>
        <p className="dict-subtitle">Digite uma palavra em português para ver a tradução em inglês</p>
      </div>
      
      <div className="dictionary-body">
        <form onSubmit={searchReverso} className="dictionary-form">
          <div className="input-with-icon">
            <FaSearch className="search-icon" />
            <input
              type="text"
              value={word}
              onChange={(e) => setWord(e.target.value)}
              placeholder="Type a word in Portuguese"
              className="dictionary-input"
              disabled={loading}
            />
          </div>
          <button 
            type="submit" 
            className="dictionary-button"
            disabled={!word.trim()}
          >
            <FaExternalLinkAlt style={{ marginRight: '8px' }} />
            Search on Reverso
          </button>
        </form>

        <div className="dictionary-hint">
          <p>Clique no ícone de alto-falante para ouvir a pronúncia.</p>
          <p>O histórico mostra as últimas 5 palavras pesquisadas.</p>
        </div>

        {history.length > 0 && (
          <div className="translation-history">
            <h4>History:</h4>
            <ul>
              {history.map((item) => (
                <li key={item.id || item.word} className="history-item">
                  <span className="word">{item.word}</span>
                  <span className="separator">→</span>
                  <span className="translation" title={item.englishWord || item.translation}>
                    {item.translation}
                  </span>
                  <div className="item-actions">
                    <button 
                      className="icon-btn" 
                      onClick={() => speakWord(item, 'en-US')}
                      title="Listen to pronunciation"
                      disabled={item.translation === 'Translating...'}
                    >
                      <FaVolumeUp size={14} />
                    </button>
                    <button 
                      className="icon-btn"
                      onClick={() => copyToClipboard(item.englishWord || item.translation)}
                      title="Copy"
                      disabled={item.translation === 'Translating...'}
                    >
                      <FaCopy size={14} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
